/* Shared text-to-speech voice helpers used by both the app (VoiceManager, event editor) and Cloud Functions. */

const GEMINI_TTS_MODEL = "gemini-2.5-pro-tts";

/* ElevenLabs models selectable per event. rate is null when the model has no speed control. */
const ELEVENLABS_MODELS = [{
  id: "eleven_v4",
  label: "Eleven v4",
  rate: null
}, {
  id: "eleven_multilingual_v2",
  label: "Multilingual v2",
  rate: {
    min: 0.7,
    max: 1.2
  }
}, {
  id: "eleven_flash_v2_5",
  label: "Flash v2.5",
  rate: {
    min: 0.7,
    max: 1.2
  }
}];
const ELEVENLABS_DEFAULT_MODEL = "eleven_v4";
const ELEVENLABS_LEGACY_MODEL = "eleven_multilingual_v2";
const DEFAULT_RATE_LIMITS = {
  min: 0.5,
  max: 3
};

/* Retired voices mapped to a comparable Chirp3-HD voice of the same locale and gender. */
const legacyVoiceMap = {
  /* Google Studio voices (removed due to cost) */
  "google:en-GB-Studio-B": {
    name: "en-GB-Chirp3-HD-Charon",
    language: "en-GB",
    gender: "male"
  },
  "google:en-GB-Studio-C": {
    name: "en-GB-Chirp3-HD-Kore",
    language: "en-GB",
    gender: "female"
  },
  "google:en-US-Studio-Q": {
    name: "en-US-Chirp3-HD-Charon",
    language: "en-US",
    gender: "male"
  },
  "google:en-US-Studio-O": {
    name: "en-US-Chirp3-HD-Kore",
    language: "en-US",
    gender: "female"
  },
  "google:fr-FR-Studio-D": {
    name: "fr-FR-Chirp3-HD-Charon",
    language: "fr-FR",
    gender: "male"
  },
  "google:fr-FR-Studio-A": {
    name: "fr-FR-Chirp3-HD-Kore",
    language: "fr-FR",
    gender: "female"
  },
  "google:de-DE-Studio-B": {
    name: "de-DE-Chirp3-HD-Charon",
    language: "de-DE",
    gender: "male"
  },
  "google:de-DE-Studio-C": {
    name: "de-DE-Chirp3-HD-Kore",
    language: "de-DE",
    gender: "female"
  },
  "google:es-ES-Studio-F": {
    name: "es-ES-Chirp3-HD-Charon",
    language: "es-ES",
    gender: "male"
  },
  "google:es-ES-Studio-C": {
    name: "es-ES-Chirp3-HD-Kore",
    language: "es-ES",
    gender: "female"
  },
  "google:es-US-Studio-B": {
    name: "es-US-Chirp3-HD-Charon",
    language: "es-US",
    gender: "male"
  },
  /* Deepgram Aura voices (Deepgram TTS removed) */
  "deepgram:aura-2-hyperion-en": {
    name: "en-AU-Chirp3-HD-Charon",
    language: "en-AU",
    gender: "male"
  },
  "deepgram:aura-2-draco-en": {
    name: "en-GB-Chirp3-HD-Charon",
    language: "en-GB",
    gender: "male"
  }
};

/* Returns the replacement voice for a retired voice, or the original voice when no remap is needed. */
function migrateVoice(voice) {
  if (!voice || !voice.provider || !voice.name) return voice;
  let replacement = legacyVoiceMap[`${voice.provider}:${voice.name}`];
  if (!replacement && voice.provider === "deepgram") {
    /* Unknown Deepgram voice - fall back to a US English voice of the same gender */
    replacement = voice.gender === "female" ? {
      name: "en-US-Chirp3-HD-Kore",
      language: "en-US",
      gender: "female"
    } : {
      name: "en-US-Chirp3-HD-Charon",
      language: "en-US",
      gender: "male"
    };
  }
  if (!replacement) return voice;
  return {
    ...voice,
    provider: "google",
    id: undefined,
    name: replacement.name,
    language: replacement.language,
    gender: replacement.gender,
    styles: ["default"]
  };
}
function isLegacyVoice(voice) {
  return !!voice && (voice.provider === "deepgram" || !!legacyVoiceMap[`${voice.provider}:${voice.name}`]);
}

/* Unique identity for a voice. Names alone are not unique (e.g. Polly voices on several engines, Gemini voices across locales). */
function voiceKey(voice) {
  if (!voice) return "";
  return [voice.provider, voice.id || voice.voiceURI || voice.name, voice.engine || "", voice.language || ""].join(":");
}
function isDragonHdVoice(voice) {
  return !!voice && voice.provider === "microsoft" && /:DragonHD(?!Flash)[A-Za-z]*Neural$/.test(voice.name || "");
}

/*
 * The ElevenLabs model is stored in the event's speakingStyle.
 * Events saved before model selection existed ("default" style, voice without eleven_v4 in its styles)
 * keep rendering with Multilingual v2; voices assigned from the current Voice Manager default to v4.
 */
function resolveElevenLabsModel(voice, speakingStyle) {
  if (ELEVENLABS_MODELS.some(model => model.id === speakingStyle)) return speakingStyle;
  if (voice && Array.isArray(voice.styles) && voice.styles.includes(ELEVENLABS_DEFAULT_MODEL)) return ELEVENLABS_DEFAULT_MODEL;
  return ELEVENLABS_LEGACY_MODEL;
}

/* Speaking rate range for a voice, or null when the voice/model controls its own pacing. */
function rateLimits(voice, speakingStyle) {
  if (!voice) return DEFAULT_RATE_LIMITS;
  /* Gemini-TTS and Azure DragonHD voices don't accept a speaking rate */
  if (voice.provider === "gemini" || isDragonHdVoice(voice)) return null;
  if (voice.provider === "ElevenLabs") {
    const modelId = resolveElevenLabsModel(voice, speakingStyle);
    return ELEVENLABS_MODELS.find(model => model.id === modelId).rate;
  }
  return DEFAULT_RATE_LIMITS;
}
function supportsRate(voice, speakingStyle) {
  return rateLimits(voice, speakingStyle) !== null;
}

/* Model id sent to the TTS function for a voice (Gemini and ElevenLabs), otherwise undefined */
function renderModel(voice, speakingStyle) {
  if (!voice) return undefined;
  if (voice.provider === "ElevenLabs") return resolveElevenLabsModel(voice, speakingStyle);
  return voice.model;
}

/*
 * Speaking style sent to the TTS function. Only ElevenLabs uses speakingStyle (as its model id);
 * other providers always get "default" because inline emotion tags replace per-event styles.
 */
function renderStyle(voice, speakingStyle) {
  if (voice && voice.provider === "ElevenLabs") return speakingStyle;
  return "default";
}

/* Model list shown as the ElevenLabs voice's selectable styles */
function elevenLabsStyles() {
  return ELEVENLABS_MODELS.map(model => model.id);
}

/* Post-render playback speed range. A single ffmpeg atempo stage accepts 0.5-2.0. */
const SPEED_LIMITS = {
  min: 0.5,
  max: 2
};
function clampSpeed(speed) {
  const value = parseFloat(speed);
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.min(SPEED_LIMITS.max, Math.max(SPEED_LIMITS.min, value));
}

/* Audible length of an event's rendered audio once its playback speed is applied (seconds). */
function effectiveAudioDuration(event) {
  if (!event) return 0;
  const duration = Number(event.audioFileDuration);
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return duration / clampSpeed(event.speed);
}

/* ffmpeg atempo filter chain for a speed, or "" when no change is needed. */
function atempoChain(speed) {
  let remaining = clampSpeed(speed);
  if (Math.abs(remaining - 1) < 0.001) return "";
  const stages = [];
  while (remaining > 2) {
    stages.push("atempo=2.0");
    remaining /= 2;
  }
  while (remaining < 0.5) {
    stages.push("atempo=0.5");
    remaining /= 0.5;
  }
  stages.push(`atempo=${remaining.toFixed(4)}`);
  return stages.join(",");
}

/* Bracketed delivery tags such as [laughs] or [whispers], interpreted by expressive models. */
const EMOTION_TAG_REGEX = /\[[^\[\]\n]{1,40}\]/g;
const EMOTION_TAGS = ["laughs", "chuckles", "sighs", "whispers", "excited", "sad", "angry", "sarcastic", "curious", "crying", "shouting", "pause"];
function supportsEmotionTags(voice, speakingStyle) {
  if (!voice) return false;
  if (voice.provider === "gemini") return true;
  if (voice.provider === "ElevenLabs") return resolveElevenLabsModel(voice, speakingStyle) === "eleven_v4";
  return false;
}
function stripEmotionTags(text) {
  if (!text) return "";
  return text.replace(EMOTION_TAG_REGEX, " ").replace(/[ \t]{2,}/g, " ").replace(/[ \t]+([,.!?;:])/g, "$1").replace(/^[ \t]+|[ \t]+$/gm, "").trim();
}

/* Plain text as it should be sent to a voice: tags kept for expressive models, stripped otherwise. */
function prepareTtsText(text, voice, speakingStyle) {
  if (!text) return "";
  return supportsEmotionTags(voice, speakingStyle) ? text.trim() : stripEmotionTags(text);
}
export default {
  GEMINI_TTS_MODEL,
  ELEVENLABS_MODELS,
  ELEVENLABS_DEFAULT_MODEL,
  ELEVENLABS_LEGACY_MODEL,
  resolveElevenLabsModel,
  rateLimits,
  renderModel,
  renderStyle,
  elevenLabsStyles,
  legacyVoiceMap,
  migrateVoice,
  isLegacyVoice,
  voiceKey,
  isDragonHdVoice,
  supportsRate,
  SPEED_LIMITS,
  clampSpeed,
  effectiveAudioDuration,
  atempoChain,
  EMOTION_TAG_REGEX,
  EMOTION_TAGS,
  supportsEmotionTags,
  stripEmotionTags,
  prepareTtsText
};